import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { createSafeHtml } from "@/lib/sanitize";

interface Slide {
  title: string;
  content: string;
  image?: string;
  notes?: string;
}

interface SlidePresentationProps {
  slides: Slide[];
  onComplete?: () => void;
}

export function SlidePresentation({ slides, onComplete }: SlidePresentationProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const goToNextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else if (onComplete) {
      onComplete();
    }
  };

  const goToPreviousSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!slides || slides.length === 0) {
    return (
      <div className="text-center text-muted-foreground p-8">
        No slides available
      </div>
    );
  }

  const slide = slides[currentSlide];

  return (
    <div className={cn(
      "space-y-4",
      isFullscreen && "fixed inset-0 z-50 bg-background p-8 flex flex-col"
    )}>
      {/* Slide Container */}
      <div className={cn(
        "relative bg-gradient-to-br from-primary/5 via-background to-accent/5 border-2 border-border/50 rounded-xl overflow-hidden shadow-xl",
        isFullscreen ? "flex-1" : "aspect-[16/9]"
      )}>
        <div className="absolute inset-0 p-8 flex flex-col">
          {/* Slide Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                {slide.title}
              </h2>
              <Badge variant="secondary" className="text-xs font-medium shadow-sm">
                Slide {currentSlide + 1} of {slides.length}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              className="h-8 w-8 transition-all hover:scale-110"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Slide Content */}
          <div className="flex-1 flex flex-col md:flex-row gap-6 overflow-auto">
            <div className="flex-1 space-y-4">
              {slide.image && (
                <img
                  loading="lazy"
                  src={slide.image}
                  alt={slide.title}
                  className="w-full rounded-lg border-2 border-border/50 shadow-lg object-cover max-h-64"
                />
              )}
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <div dangerouslySetInnerHTML={createSafeHtml(slide.content)} />
              </div>
            </div>

            {slide.notes && (
              <div className="md:w-72 bg-muted/50 rounded-lg p-4 border border-border/50 shadow-sm">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <span className="text-primary">📝</span> Speaker Notes
                </h4>
                <p className="text-sm text-muted-foreground">{slide.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-between gap-4 p-4 bg-gradient-to-r from-muted/50 to-muted/30 rounded-lg border border-border/50 shadow-sm">
        <Button
          variant="outline"
          onClick={goToPreviousSlide}
          disabled={currentSlide === 0}
          className="transition-all hover:scale-105 disabled:opacity-50"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        {/* Slide Indicators */}
        <div className="flex gap-2 flex-wrap justify-center">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={cn(
                "h-2 rounded-full transition-all hover:scale-110",
                index === currentSlide
                  ? "w-8 bg-primary shadow-lg shadow-primary/50"
                  : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <Button
          onClick={goToNextSlide}
          variant={currentSlide === slides.length - 1 ? "default" : "outline"}
          className="transition-all hover:scale-105 shadow-sm hover:shadow-md"
        >
          {currentSlide === slides.length - 1 ? (
            "Complete"
          ) : (
            <>
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>

      {/* Keyboard Shortcuts Hint */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <kbd className="px-2 py-1 bg-muted rounded border border-border text-foreground font-mono text-xs">←</kbd>
        <kbd className="px-2 py-1 bg-muted rounded border border-border text-foreground font-mono text-xs">→</kbd>
        <span>to navigate</span>
      </div>
    </div>
  );
}
